import {
    Avatar,
    Badge,
    Box,
    Divider,
    Flex,
    HStack,
    Heading,
    IconButton,
    SimpleGrid,
    Text,
    Tooltip,
    VStack,
    useColorModeValue,
    useDisclosure,
} from '@chakra-ui/react';
import { useState } from 'react';
import { FaTrash, FaUsers } from 'react-icons/fa';
import { translate } from '../../../i18n';
import { SportType } from '../../../models/court/dto/court.dto';
import { EventParticipantDto } from '../../../models/event/dto/event-participant.dto';
import { EventTeamDto } from '../../../models/event/dto/event-team.dto';
import { EventDto, EventStatus } from '../../../models/event/dto/event.dto';
import { UserDto } from '../../../models/user/dto/user.dto';
import { useNavigateToUserDetails } from '../../_shared/calendar/hooks/useNavigateToUserDetails';
import KickEventParticipantModal from './KickEventParticipantModal';
import { useIsOrgManager } from '../../auth/roles';

interface EventParticipantsCardProps {
    event: EventDto;
}

// Helper function to get sport level for a user for a specific sport type
const getSportLevel = (user: UserDto, sportType: SportType) => {
    if (!user.sportLevels || user.sportLevels.length === 0) {
        return null;
    }
    return user.sportLevels.find(sl => sl.sportType === sportType) || null;
};

// Helper function to check if user's sport level is verified (level verified by admin). Supports legacy isValidated.
const isSportLevelVerified = (user: UserDto, sportType: SportType) => {
    const sportLevel = getSportLevel(user, sportType);
    return !!sportLevel?.isLevelVerified;
};

const SportLevelInfo: React.FC<{ user: UserDto; sportType?: SportType; variant: 'compact' | 'card' }> = ({ user, sportType, variant }) => {
    if (!sportType) return null;
    const sportLevel = getSportLevel(user, sportType);
    if (!sportLevel) return null;
    const color = variant === 'compact' ? 'gray.500' : 'gray.600';
    return (
        <>
            {sportLevel.identificationNumber && (
                <Text fontSize="xs" color={color}>
                    {variant === 'compact' ? translate('event.license_number') : translate('event.id_label')}: {sportLevel.identificationNumber}
                </Text>
            )}
            {sportLevel.officialRanking && (
                <Text fontSize="xs" color={color}>
                    {translate('event.ranking')}: {sportLevel.officialRanking}
                </Text>
            )}
        </>
    );
};

const EventParticipantsCard: React.FC<EventParticipantsCardProps> = ({ event }) => {
    const eventSportType = event.sportType;
    const navigateToUserDetails = useNavigateToUserDetails();
    const isOrgManager = useIsOrgManager();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [participantToKick, setParticipantToKick] = useState<EventParticipantDto | null>(null);
    const cardBg = useColorModeValue('white', 'gray.700');
    const rowBg = useColorModeValue('gray.50', 'gray.600');
    const rowHoverBg = useColorModeValue('gray.100', 'gray.500');
    const emptySlotBorderColor = useColorModeValue('gray.300', 'gray.500');

    const handleKickClick = (participant: EventParticipantDto) => {
        setParticipantToKick(participant);
        onOpen();
    };

    const handleModalClose = () => {
        setParticipantToKick(null);
        onClose();
    };

    if ((!event.participants || event.participants.length === 0) && (!event.teams || event.teams.length === 0)) {
        return (
            <Box textAlign="center" py={8}>
                <Text fontSize="lg" color="gray.500">
                    {event.teamBased
                        ? (translate('event.no_teams') || 'No teams yet')
                        : (translate('event.no_participants') || 'No participants yet')}
                </Text>
            </Box>
        );
    }

    // If this is a team-based event, show teams view
    if (event.teamBased && event.teams && event.teams.length > 0) {
        const maxTeams = Math.floor(event.capacity / (event.teamSize || 1));
        const availableTeamSlots = maxTeams - event.teams.length;

        return (
            <VStack spacing={4} align="stretch" p={4}>
                {/* Status badge */}
                <Flex justify="space-between" align="center" mb={2}>
                    <HStack>
                        <FaUsers />
                        <Text fontWeight="semibold">
                            {translate('event.team_size') || 'Team Size'}: {event.teamSize} {translate('event.players_per_team') || 'players per team'}
                        </Text>
                    </HStack>
                    <Badge colorScheme={availableTeamSlots === 0 ? 'red' : 'green'}>
                        {availableTeamSlots === 0
                            ? translate('event.full') || 'Full'
                            : `${availableTeamSlots} ${availableTeamSlots !== 1 ? translate('event.teams_available') || 'teams available' : translate('event.team_available') || 'team available'}`}
                    </Badge>
                </Flex>

                {/* Teams Grid */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {event.teams.map((team: EventTeamDto, teamIndex: number) => (
                        <Box
                            key={team.id}
                            p={5}
                            borderWidth="2px"
                            borderRadius="lg"
                            bg={cardBg}
                            borderColor={team.status === 'complete' ? 'green.300' : 'orange.300'}
                        >
                            <VStack align="stretch" spacing={3}>
                                {/* Team Header */}
                                <Flex justify="space-between" align="center">
                                    <HStack>
                                        <Heading size="sm">
                                            {translate('event.team') || 'Team'} #{teamIndex + 1}
                                        </Heading>
                                        <Badge colorScheme={team.status === 'complete' ? 'green' : 'orange'}>
                                            {team.status === 'complete'
                                                ? translate('event.team_complete') || 'Complete'
                                                : translate('event.team_incomplete') || 'Incomplete'}
                                        </Badge>
                                    </HStack>
                                    <Text fontSize="sm" color="gray.500">
                                        {team.participants.length}/{team.size}
                                    </Text>
                                </Flex>

                                <Divider />

                                {/* Team Members */}
                                <VStack align="stretch" spacing={2}>
                                    {team.participants.map((participant: EventParticipantDto) => (
                                        <Flex
                                            key={participant.participant.id}
                                            align="center"
                                            justify="space-between"
                                            p={3}
                                            borderRadius="md"
                                            bg={rowBg}
                                            _hover={{ bg: rowHoverBg }}
                                            cursor={navigateToUserDetails ? "pointer" : undefined}
                                            onClick={navigateToUserDetails ? () => navigateToUserDetails(participant.participant.id) : undefined}
                                        >
                                            <HStack>
                                                <Avatar
                                                    size="sm"
                                                    name={`${participant.participant.firstName} ${participant.participant.lastName}`}
                                                    src={participant.participant.avatar}
                                                />
                                                <VStack align="start" spacing={0}>
                                                    <HStack>
                                                        <Text fontWeight="medium" fontSize="sm">
                                                            {participant.participant.firstName} {participant.participant.lastName}
                                                        </Text>
                                                        {isSportLevelVerified(participant.participant, eventSportType) && (
                                                            <Badge colorScheme="green" size="sm">
                                                                {translate('user.license_verified')}
                                                            </Badge>
                                                        )}
                                                        {participant.role === 'leader' && (
                                                            <Badge colorScheme="brand" size="sm">
                                                                {translate('event.team_leader') || 'Leader'}
                                                            </Badge>
                                                        )}
                                                    </HStack>
                                                    <Text fontSize="xs" color="gray.500">
                                                        {translate('event.level') || 'Level'}: {participant.participant.level}
                                                    </Text>
                                                    <SportLevelInfo user={participant.participant} sportType={eventSportType} variant="compact" />
                                                </VStack>
                                            </HStack>

                                            {isOrgManager && event.status !== EventStatus.CANCELLED && (
                                                <Tooltip label={translate('event.remove_from_team') || 'Remove from team'}>
                                                    <IconButton
                                                        aria-label={translate('event.remove_from_team') || 'Remove from team'}
                                                        icon={<FaTrash />}
                                                        size="xs"
                                                        colorScheme="red"
                                                        variant="ghost"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleKickClick(participant);
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Flex>
                                    ))}

                                    {/* Show empty slots */}
                                    {Array.from({ length: team.size - team.participants.length }).map((_, i) => (
                                        <Flex
                                            key={`empty-${i}`}
                                            align="center"
                                            p={3}
                                            borderRadius="md"
                                            bg={rowBg}
                                            borderWidth="1px"
                                            borderStyle="dashed"
                                            borderColor={emptySlotBorderColor}
                                        >
                                            <HStack>
                                                <Avatar size="sm" bg="gray.300" />
                                                <Text fontSize="sm" color="gray.400">
                                                    {translate('event.awaiting_team_member') || 'Awaiting team member...'}
                                                </Text>
                                            </HStack>
                                        </Flex>
                                    ))}
                                </VStack>
                            </VStack>
                        </Box>
                    ))}
                </SimpleGrid>

                {/* Kick Participant Modal */}
                <KickEventParticipantModal
                    isOpen={isOpen}
                    onClose={handleModalClose}
                    eventId={event.id}
                    participant={participantToKick}
                />
            </VStack>
        );
    }

    // Individual event view (original code)
    const participants = event.participants || [];
    return (
        <VStack spacing={4} align="stretch" p={4}>
            {/* Status badge */}
            <Flex justify="flex-end" align="center" mb={2}>
                <Badge colorScheme={participants.length === event.capacity ? 'red' : 'green'}>
                    {participants.length === event.capacity
                        ? translate('event.full') || 'Full'
                        : `${event.capacity - participants.length} ${translate('event.spots_available') || 'spots available'}`}
                </Badge>
            </Flex>

            {/* Participants Grid */}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {participants.map((participant: EventParticipantDto, index: number) => (
                    <Box
                        key={participant.participant.id}
                        p={5}
                        borderWidth="1px"
                        borderRadius="lg"
                        bg={cardBg}
                        _hover={{
                            bg: rowBg,
                            transform: 'translateY(-2px)',
                            boxShadow: 'md'
                        }}
                        transition="all 0.2s"
                        position="relative"
                    >
                        {/* Remove button - admin-only (API rejects coach kicks), and only if not cancelled */}
                        {isOrgManager && event.status !== EventStatus.CANCELLED && (
                            <Tooltip label={translate('event.remove_participant') || 'Remove Participant'}>
                                <IconButton
                                    aria-label={translate('event.remove_participant') || 'Remove Participant'}
                                    icon={<FaTrash />}
                                    size="sm"
                                    colorScheme="red"
                                    variant="outline"
                                    position="absolute"
                                    top={2}
                                    right={2}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleKickClick(participant);
                                    }}
                                />
                            </Tooltip>
                        )}

                        <VStack
                            spacing={3}
                            cursor={navigateToUserDetails ? "pointer" : undefined}
                            onClick={navigateToUserDetails ? () => navigateToUserDetails(participant.participant.id) : undefined}
                        >
                            <Avatar
                                size="md"
                                name={`${participant.participant.firstName} ${participant.participant.lastName}`}
                                src={participant.participant.avatar}
                            />

                            <VStack spacing={1} textAlign="center">
                                <HStack spacing={2} justify="center" flexWrap="wrap">
                                    <Text fontWeight="semibold" fontSize="md">
                                        {participant.participant.firstName} {participant.participant.lastName}
                                    </Text>
                                    {isSportLevelVerified(participant.participant, eventSportType) && (
                                        <Badge colorScheme="green" size="sm">
                                            {translate('user.license_verified')}
                                        </Badge>
                                    )}
                                </HStack>
                                <Text fontSize="sm" color="gray.500">
                                    {participant.participant.email}
                                </Text>
                                <HStack spacing={2}>
                                    <Badge colorScheme="brand" size="sm">
                                        {translate('event.level') || 'Level'}: {participant.participant.level}
                                    </Badge>
                                    <Badge colorScheme="purple" size="sm">
                                        #{index + 1}
                                    </Badge>
                                </HStack>
                                <VStack spacing={0.5} mt={1}>
                                    <SportLevelInfo user={participant.participant} sportType={eventSportType} variant="card" />
                                </VStack>
                            </VStack>
                        </VStack>
                    </Box>
                ))}
            </SimpleGrid>

            {/* Kick Participant Modal */}
            <KickEventParticipantModal
                isOpen={isOpen}
                onClose={handleModalClose}
                eventId={event.id}
                participant={participantToKick}
            />
        </VStack>
    );
};

export default EventParticipantsCard;
